import { Trans } from '@lingui/react/macro';
import { TokenResult } from '@stripe/stripe-js';

import Box from '@/design_system/Box';
import Button from '@/design_system/Button';
import InputText from '@/design_system/InputText';
import Stack from '@/design_system/Stack';
import InlineText from '@/design_system/Typography/InlineText';
import Paragraph from '@/design_system/Typography/Paragraph';
import IconCheck from '@/icons/Check.svg';
import IconClock from '@/icons/Clock.svg';
import { getUserWorkshops, hasPermission } from '@/models/user';
import { useWorkshop } from '@/models/workshop';
import { useCurrentSession } from '@/services/auth';
import { useGetOnboardingLink, useStripe, useStripeAccount } from '@/services/stripe';

const Workshop = () => {
  const { currentSession } = useCurrentSession();

  const workshopId = getUserWorkshops(currentSession)?.[0]?.id;
  const { data: workshop } = useWorkshop(workshopId);

  if (!workshop) {
    return null;
  }

  const canManageBilling = hasPermission(currentSession, 'manage_billing', {
    entityId: workshop.id,
  });

  const hasPayoutHandledByProlong = workshop.entityOrganizations.some(
    (entityOrganization) =>
      entityOrganization.workshopConfig?.payoutStrategy === 'handled-by-prolong'
  );

  return (
    <Stack gap="16px">
      <InputText
        label={<Trans id="settings.workshop.name.label">Name</Trans>}
        value={workshop.name}
        style={{ maxWidth: '300px' }}
        isDisabled
      />
      {canManageBilling && hasPayoutHandledByProlong && <StripeAccount workshop={workshop} />}
    </Stack>
  );
};

const StripeAccount = ({ workshop }: { workshop: { companyId: string | null } }) => {
  const stripe = useStripe();
  const { data: { id, accountStatus } = {} } = useStripeAccount({
    companyId: workshop.companyId,
  });
  const { mutateAsync: getOnboardingLink, isPending: isGetOnboardingLinkPending } =
    useGetOnboardingLink();

  return (
    <Box>
      <Stack row gap="8px" justifyContent="space-between" alignItems="center">
        <InlineText weight="medium">
          <Trans id="settings.workshop.stripe.label">Stripe account</Trans>
        </InlineText>
        {accountStatus === 'complete' && (
          <Stack row gap="8px" alignItems="center">
            <IconCheck />
            <Paragraph>
              <Trans id="settings.workshop.status.ready">Ready</Trans>
            </Paragraph>
          </Stack>
        )}
        {accountStatus === 'pending' && (
          <Stack row gap="8px" alignItems="center">
            <IconClock />
            <Paragraph>
              <Trans id="settings.workshop.status.pending">Pending verification</Trans>
            </Paragraph>
          </Stack>
        )}
        {(accountStatus === 'none' || accountStatus === 'onboarding') && (
          <Button
            variant="primary"
            isLoading={!stripe || isGetOnboardingLinkPending}
            onPress={async () => {
              let stripeAccount: TokenResult | undefined = undefined;

              if (!id) {
                stripeAccount = await stripe!.createToken('account', {
                  business_type: 'company',
                  tos_shown_and_accepted: true,
                });
              }

              const { url } = await getOnboardingLink({
                companyId: workshop.companyId!,
                accountTokenId: stripeAccount?.token?.id,
              });

              window.location.href = url;
            }}
          >
            <Trans id="settings.workshop.status.finish-onboarding">Finish onboarding</Trans>
          </Button>
        )}
      </Stack>
    </Box>
  );
};

export default Workshop;
